import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Column } from "primereact/column";
import DefMembershipActions from "../../Redux/actions/defMembership-actions";
import BusinessCustomersActions from "../../Redux/transition/customers/business-customer.reducer";
import { Trans, useTranslation } from "react-i18next";
import Loading from "../../Components/Loading";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { CustomerSearchField } from "../../Components/CustomerSearchField";
import CustomDataTable from "../../Components/CustomDataTable";
import { Card } from "primereact/card";
import CustomerEdit from "./edit";
import "../../Styles/pages/customers.scss"
import { OverlayPanel } from 'primereact/overlaypanel';
import { ProgressSpinner } from 'primereact/progressspinner';
import InviteSubscriber from "../Subscriptions/Invitation/InviteSubscriber";
import "../../Styles/pages/inviteSubscriber.scss";
import { Dialog } from "primereact/dialog";
import CustomToast from "../../Components/CustomToast";
import { Paginator } from 'primereact/paginator';
import { Dropdown } from 'primereact/dropdown';
import { useDateFormatter } from "../../Components/FormatDateTime";
import { removePlusFromNum } from '../../Components/CleanInputs';
import { FormatCurrency, useCurrencyConstants } from '../../Components/CurrencyFormatter';
import { Checkbox } from "primereact/checkbox";
import { Menubar } from "primereact/menubar";
import SubscriberToSegment from "./Segments/subscriberToSegment";
import { normalizeSegmentsList, SEGMENT_DROPDOWN_PAGE_OPTIONS } from "./Segments/segmentUtils";
import { normalizeTagGroupsList, TAG_GROUP_DROPDOWN_PAGE_OPTIONS } from "./Segments/tagUtils";
import AddSubscribersToTagDialog from './TagComponents/AddSubscribersToTagDialog';
import RemoveSubscribersDialog from './TagComponents/RemoveSubscribersFromTagDialog';
import "../../Styles/components/tags.scss";
import { Tag } from 'primereact/tag';
import { SegmentDropdown, TagMultiselect } from "../../Components/SegmentTagsDropdown";
import RecordPayment from "./recordPayment";
import CollectPayment from "./collectPayment";
import AutoBillingBadge from "../../Components/AutoBillingBadge";
import { buildGroupedTagOptions } from "../../utils/tagGroupOptions";
import { buildSelectedCustomersCsv } from "../../utils/subscriberExportCsv";

const SUBSCRIBERS_MOBILE_BREAKPOINT = 1050;

const getSelectedTagIds = (tags = []) =>
    tags
        .map((tag) => {
            if (tag == null || tag === "") {
                return null;
            }
            if (typeof tag === "object") {
                return tag.value ?? tag.id;
            }
            return tag;
        })
        .filter((id) => id != null && id !== "");

const buildBusinessCustomersExportQuery = ({ searchText, segmentId, tags } = {}) => {
    const params = [];
    const tagIds = getSelectedTagIds(tags);

    if (searchText) {
        params.push(`searchText=${encodeURIComponent(String(searchText).toLowerCase())}`);
    }
    if (tagIds.length > 0) {
        params.push(`tags=${tagIds.join("|")}`);
    }
    if (segmentId) {
        params.push(`segmentId=${segmentId}`);
    }

    return params.length ? `?${params.join("&")}` : "";
};

const downloadSelectedCustomersCsv = (customers = [], filename) => {
    const blob = new Blob([buildSelectedCustomersCsv(customers)], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename || "selected_customers.csv");
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
    link.remove();
};

const Customers = () => {
    const { formatDate } = useDateFormatter();
    const { currencyFormat, currencySymbol } = useCurrencyConstants();

    const { t } = useTranslation("common");
    const navigate = useNavigate();

    const [displayBasic, setDisplayBasic] = useState(false);
    const [query, setQuery] = useState("");
    const [submittedQuery, setSubmittedQuery] = useState("");
    const [showInviteSubscriber, setShowInviteSubscriber] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [latest, setLatest] = useState(null);
    const [customerNumber, setCustomerNumber] = useState(null);
    const [selectedSubscriber, setSelectedSubscriber] = useState(null);
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [confirmPause, setConfirmPause] = useState(false)
    const [confirmCancel, setConfirmCancel] = useState(false)
    const [pausedBulk, setPausedBulk] = useState(false);
    const [cancelBulk, setCancelBulk] = useState(false);
    const [pausedSingle, setPausedSingle] = useState(false);
    const [cancelSingle, setCancelSingle] = useState(false);
    const [alreadyPaused, setAlreadyPaused] = useState(false);
    const [alreadyCancelled, setAlreadyCancelled] = useState(false)
    const [toastData, setToastData] = useState({ severity: '', detail: '', life: 0 });
    const [toastTrigger, setToastTrigger] = useState(false);
    const overlayPanelMoreSubs = useRef(null);
    const overlayPanelLatest = useRef(null);
    const loadingOverlayRef = useRef(null);
    const [visibleInvite, setVisibleInvite] = useState(false);
    const [overlayContent, setOverlayContent] = useState(null);
    const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
    const [loadingOverlayTarget, setLoadingOverlayTarget] = useState(null);
    const [overlayTarget, setOverlayTarget] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [selectionType, setSelectionType] = useState(null);
    const [customerSubscriptions, setCustomerSubscriptions] = useState([]);
    const overlayRefs = useRef(new Map());
    // eslint-disable-next-line no-unused-vars
    const [activeOverlayId, setActiveOverlayId] = useState(null);
    const [addSubscriberVisible, setAddSubscriberVisible] = useState(false);
    const [switchSegVisible, setSwitchSegVisible] = useState(false);
    const [removeSubscriberVisible, setRemoveSubscriberVisible] = useState(false);
    const [displayAdd, setDisplayAdd] = useState(false);
    const [displayRemove, setDisplayRemove] = useState(false);
    const [rowSubClicked, setRowSubClicked] = useState(null);
    const [specificTagClicked, setSpecificTagClicked] = useState(false);
    const [selectedSegment, setSelectedSegment] = useState(null);
    const [selectedTags, setSelectedTags] = useState([]);
    const [csvExportRequested, setCsvExportRequested] = useState(false);
    const [isMobileStack, setIsMobileStack] = useState(
        () => typeof window !== "undefined" && window.innerWidth <= SUBSCRIBERS_MOBILE_BREAKPOINT
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia(`(max-width: ${SUBSCRIBERS_MOBILE_BREAKPOINT}px)`);
        const onBreakpointChange = (event) => setIsMobileStack(event.matches);
        onBreakpointChange(mediaQuery);
        mediaQuery.addEventListener("change", onBreakpointChange);
        return () => mediaQuery.removeEventListener("change", onBreakpointChange);
    }, []);

    const hideAllOverlays = () => {
        overlayRefs.current.forEach((overlay) => {
            if (overlay) {
                overlay.hide();
            }
        });
    };

    const handleOverlayToggle = (e, rowData) => {
        e.stopPropagation();

        const overlayId = rowData.id;
        const overlay = overlayRefs.current.get(overlayId);

        if (!overlay) return;
        hideAllOverlays();
        overlay.toggle(e);
    };

    const [state, setState] = useState({
        enterpriseCustomers: null,
        count: null,
        page: 0,
        sort: "addedOn,desc",
        size: 20,
        loading: true,
        visible: false,
        first: 0,
        businessCustomers: null,
        filteredEnterpriseCustomersObjects: null,
        popupClicked: false,
        name: "",
        firstName: "",
        lastName: "",
        customerNum: "",
        email: "",
        nameError: false,
        firstNameError: false,
        lastNameError: false,
        data: null,
        updating: false,
        success: false,
        id: null,
        business: null,
        customers: null,
        isFiltered: false,
        customerId: null,
        query: null,
        initialTier: null,
        selectedCustomersIds: [],
        customerNumber: null,
        pausingBulk: false,
        customerActiveSubs: null,
        customerCancelledSubs: null,
        customerPausedSubs: null,
        customerCompletedSubs: null,
        showRecordPopup: false,
        showCollectPopup: false,
        treePlanSelected: null,
        treePlanSelectedKey: null,
        currentSubscriber: null,
        oneOffOnly: false

    });

    const routeChange = (pathname, state) => {
        navigate(pathname, state)
    };

    const defMembership = useSelector((state) => state.defMembership.defMembership);
    const isAdminOrOwner = defMembership?.role === "OWNER" || defMembership?.role === "ADMIN";
    const businessCustomers = useSelector((state) => state.businessCustomers.businessCustomers);
    const filteredCustomers = useSelector((state) => state.businessCustomers.filteredCustomers);
    const fetchingBusinessCustomerSummarySearch = useSelector((state) => state.businessCustomers.fetchingBusinessCustomerSummarySearch);
    const fetchingBusinessCustomerSummary = useSelector((state) => state.businessCustomers.fetchingBusinessCustomerSummary);
    const businessCustomersSummarySearch = useSelector((state) => state.businessCustomers.businessCustomerSummarySearch);
    const businessCustomersSummary = useSelector((state) => state.businessCustomers.businessCustomerSummary);
    const customerReceivedPayments = useSelector((state) => state.businessCustomers.customerReceivedPayments)
    const fetchingCustomerReceivedPayment = useSelector((state) => state.businessCustomers.fetchingCustomerReceivedPayments)
    const pausingBulkSubscriptions = useSelector((state) => state.businessCustomers.pausingBulkBusinessCustomers)
    const pausingSingleSubscriptions = useSelector((state) => state.businessCustomers.pausingBusinessCustomersSinglePause)
    const pausedBulkSubscriptions = useSelector((state) => state.businessCustomers.businessCustomersBulkPause)
    const pausedSingleSubscriptions = useSelector((state) => state.businessCustomers.businessCustomersSinglePause)
    const cancelledBulkSubscriptions = useSelector((state) => state.businessCustomers.businessCustomersBulkCancel)
    const cancelledSingleSubscriptions = useSelector((state) => state.businessCustomers.businessCustomersSingleCancel)
    const cancellingBulkSubscriptions = useSelector((state) => state.businessCustomers.cancellingBulkBusinessCustomers)
    const cancellingSingleSubscriptions = useSelector((state) => state.businessCustomers.cancellingSingleBusinessCustomer)
    const { segmentSuccess, tagGroupsGraphed, tagGroups, tags } = useSelector((state) => state.businessCustomers)
    const segmentList = useMemo(() => normalizeSegmentsList(segmentSuccess), [segmentSuccess]);
    const exportingBusinessCustomersCsv = useSelector((state) => state.businessCustomers.exportingBusinessCustomersCsv)
    const exportBusinessCustomersCsvError = useSelector((state) => state.businessCustomers.exportBusinessCustomersCsvError)

    const dispatch = useDispatch();
    const getMembership = useCallback(() => { dispatch(DefMembershipActions.defMembershipRequest()) }, [dispatch]);
    const getSummaryCustomerSearch = useCallback((businessId, query) => dispatch(BusinessCustomersActions.businessCustomerSummarySearchRequest(businessId, query)), [dispatch]);
    const getSummaryCustomers = useCallback((businessId, options) => dispatch(BusinessCustomersActions.businessCustomerSummaryRequest(businessId, options)), [dispatch]);
    const getCustomerSubscription = useCallback((businessCustomerId,) => dispatch(BusinessCustomersActions.customerReceivedPaymentRequest(businessCustomerId)), [dispatch]);
    const getSegment = useCallback(
        (businessId) =>
            dispatch(
                BusinessCustomersActions.fetchingSegmentRequest(
                    businessId,
                    SEGMENT_DROPDOWN_PAGE_OPTIONS
                )
            ),
        [dispatch]
    );
    const pauseBulkCustomersSubscriptions = useCallback((membershipId, ids) => { dispatch(BusinessCustomersActions.businessCustomersBulkPauseRequest(membershipId, ids)) }, [dispatch])
    const cancelBulkCustomersSubscriptions = useCallback((membershipId, ids) => { dispatch(BusinessCustomersActions.businessCustomersBulkCancelRequest(membershipId, ids)) }, [dispatch])
    const pauseSingleCustomersSubscriptions = useCallback((businessCustomerId, membershipId) => { dispatch(BusinessCustomersActions.businessCustomersSinglePauseRequest(businessCustomerId, membershipId)) }, [dispatch])
    const cancelSingleCustomersSubscriptions = useCallback((businessCustomerId, membershipId) => { dispatch(BusinessCustomersActions.businessCustomersSingleCancelRequest(businessCustomerId, membershipId)) }, [dispatch]);
    const getTags = useCallback((businessId) => dispatch(BusinessCustomersActions.tagsRequest(businessId)), [dispatch]);
    const getTagGroups = useCallback(
        (businessId) =>
            dispatch(
                BusinessCustomersActions.tagGroupRequest(businessId, TAG_GROUP_DROPDOWN_PAGE_OPTIONS)
            ),
        [dispatch]
    );
    const getTagGroupsGraphed = useCallback((businessId) => dispatch(BusinessCustomersActions.tagGroupGraphedRequest(businessId)), [dispatch]);
    const exportBusinessCustomersCsv = useCallback((businessId, query, filename) => dispatch(BusinessCustomersActions.exportBusinessCustomersCsvRequest(businessId, query, filename)), [dispatch]);

    const pageOptions = useMemo(
        () => ({ page: state.page, size: state.size, sort: state.sort }),
        [state.page, state.size, state.sort]
    );

    const refreshCustomers = useCallback(() => {
        if (!defMembership) {
            return;
        }

        const hasFilters = selectedTags?.length > 0 || selectedSegment || submittedQuery;

        if (hasFilters) {
            getSummaryCustomerSearch(
                defMembership.business.id,
                buildBusinessCustomersExportQuery({
                    searchText: submittedQuery,
                    segmentId: selectedSegment,
                    tags: selectedTags,
                })
            );
        } else {
            getSummaryCustomers(defMembership.business.id, pageOptions);
        }
    }, [defMembership, selectedTags, selectedSegment, submittedQuery, pageOptions, getSummaryCustomerSearch, getSummaryCustomers]);

    useEffect(() => {
        if (!defMembership) {
            getMembership();
            return;
        }
        const businessId = defMembership.business.id;
        getSegment(businessId);
        getTags(businessId);
        getTagGroups(businessId);
        getTagGroupsGraphed(businessId);
    }, [defMembership, getMembership, getSegment, getTags, getTagGroups, getTagGroupsGraphed]);

    useEffect(() => {
        if (businessCustomersSummary) {
            setState((state) => {
                return {
                    ...state,
                    businessCustomers: businessCustomersSummary,
                    loading: false,
                };
            });
        }
        if (businessCustomersSummarySearch) {
            setState((state) => {
                return { ...state, filteredEnterpriseCustomersObjects: businessCustomersSummarySearch };
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessCustomers, filteredCustomers, businessCustomersSummary, businessCustomersSummarySearch]);

    useEffect(() => {
        if (!query)
            setState((state) => ({ ...state, query: null }))
    }, [query])

    useEffect(() => {
        refreshCustomers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defMembership, selectedTags, selectedSegment, submittedQuery, pageOptions.page, pageOptions.size, pageOptions.sort]);

    useEffect(() => {
        if (!csvExportRequested || exportingBusinessCustomersCsv) {
            return;
        }

        if (exportBusinessCustomersCsvError) {
            setToastData({
                severity: "error",
                summary: t("failed"),
                detail: t("business_customers.csv_error"),
                life: 5000,
            });
            setToastTrigger(true);
        }

        setCsvExportRequested(false);
    }, [csvExportRequested, exportingBusinessCustomersCsv, exportBusinessCustomersCsvError, t]);

    useEffect(() => {
        if (cancelledBulkSubscriptions !== null && cancelBulk) {
            setCancelBulk(false)
            setConfirmCancel(false)
            cancelSuccess()
        }
        if (cancelledSingleSubscriptions !== null && cancelSingle) {
            setCancelSingle(false)
            setConfirmCancel(false)
            cancelSuccess()
        }
        if (pausedBulkSubscriptions !== null && pausedBulk) {
            setConfirmPause(false)
            setPausedBulk(false)
            pauseSuccess()
        }
        if (pausedSingleSubscriptions !== null && pausedSingle) {
            setConfirmPause(false)
            setPausedSingle(false)
            pauseSuccess()
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cancellingBulkSubscriptions, cancelledBulkSubscriptions, cancelBulk,
        pausedBulkSubscriptions, pausedBulk, pausedSingle, pausedSingleSubscriptions,
        cancellingSingleSubscriptions, cancelledSingleSubscriptions, cancelSingle])

    useEffect(() => {
        if (!fetchingCustomerReceivedPayment && overlayTarget && state.popupClicked) {
            setCustomerSubscriptions(customerReceivedPayments?.content);

            loadingOverlayRef.current?.hide();
            setShowLoadingOverlay(false);

            setOverlayContent(
                <div style={{ marginTop: "0.5rem", cursor: "pointer" }}>
                    {(fetchingCustomerReceivedPayment) ? (
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>
                    ) : (
                        <span>
                            {customerSubscriptions?.map(item => (
                                <div key={item.id} style={{ display: "flex" }}>
                                    <span className={`status-outline status-${(item.status ? item.status.toLowerCase() : '')} status-content-stat`}>
                                        {item.status === "COMPLETE" ? t("business_customers.completed") : item.status}
                                    </span>
                                    <p className="item-name hover:underline" onClick={() => navigateToSubsDetails(item?.subscriber?.id)}>
                                        {item?.subscriber?.plan?.name}
                                    </p>
                                </div>
                            ))}
                        </span>
                    )}
                </div>
            );

            setTimeout(() => {
                if (overlayPanelMoreSubs.current && overlayTarget) {
                    // recalculate position
                    const freshEvent = {
                        currentTarget: overlayTarget,
                        target: overlayTarget
                    };
                    overlayPanelMoreSubs.current.show(freshEvent);
                }
            }, 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchingCustomerReceivedPayment, overlayTarget, loadingOverlayTarget, showLoadingOverlay, state.popupClicked]);

    const calenderDateFormat = defMembership?.business?.country?.jsShortDateFormat;
    const countryCurrency = defMembership?.business?.country?.currency;
    const membershipId = defMembership?.id;


    useEffect(() => {
        if (customerReceivedPayments) {
            setCustomerSubscriptions(customerReceivedPayments?.content);
        }
    }, [customerReceivedPayments, fetchingCustomerReceivedPayment, state.popupClicked])

    const singleCustomer = state.selectedCustomersIds?.length === 1;
    const singularCustomer = selectedCustomers?.length === 1;

    const handleOverlay = (e, id) => {
        e.stopPropagation();
        const targetElement = e.currentTarget;

        setLoadingOverlayTarget(targetElement);
        setShowLoadingOverlay(true);
        loadingOverlayRef.current?.show(e);

        setOverlayContent(null);
        setOverlayTarget(targetElement);

        setState((prevState) => ({ ...prevState, popupClicked: true }));

        getCustomerSubscription(id);
    };

    const hideCustomerSubscriptions = () => {
        setOverlayContent(null);
        setOverlayTarget(null);
        setState((prevState) => ({ ...prevState, popupClicked: false }));
        setState((state) => ({ ...state, customerReceivedPayments: null }));
        setCustomerSubscriptions([]);
    };

    // eslint-disable-next-line no-unused-vars
    const showOverlayLatestPanel = (event, rowData) => {
        setLatest(rowData)
        overlayPanelLatest?.current?.toggle(event);
    };

    const navigateToSubsDetails = (subsId) => {
        navigate(`/crm/customers/customer/subscription/${subsId}`);
    };

    const pauseSuccess = () => {
        const pausedBulkText = pausedBulkSubscriptions !== null && pausedBulkSubscriptions !== undefined
            ? `${pausedBulkSubscriptions} ${t('subscriber.paused-multiple')}`
            : '';
        const pausedSingleText = pausedSingleSubscriptions !== null && pausedSingleSubscriptions !== undefined
            ? `${pausedSingleSubscriptions} ${t('subscriber.paused')}`
            : '';

        const detailText = singularCustomer ? pausedSingleText : pausedBulkText;
        setToastData({
            severity: 'success',
            detail: detailText,
            life: 3000,
        });
        setToastTrigger(true);

    }
    const cancelSuccess = () => {
        const cancelledBulkText = cancelledBulkSubscriptions !== null && cancelledBulkSubscriptions !== undefined
            ? `${cancelledBulkSubscriptions} ${t('subscriber.cancelled-multiple')}`
            : '';
        const cancelledSingleText = cancelledSingleSubscriptions !== null && cancelledSingleSubscriptions !== undefined
            ? `${cancelledSingleSubscriptions} ${t('subscriber.cancelled')}`
            : '';

        const detailText = singularCustomer ? cancelledSingleText : cancelledBulkText;

        setToastData({
            severity: 'success',
            detail: detailText,
            life: 3000,
        });
        setToastTrigger(true);
    }

    const groupedOptions = useMemo(
        () =>
            buildGroupedTagOptions(
                tagGroupsGraphed,
                normalizeTagGroupsList(tagGroups),
                tags
            ),
        [tagGroupsGraphed, tagGroups, tags]
    );

    const handlePauseBulk = () => {
        const customerId = selectedCustomers.map(customer => customer.businessCustomer.id)
        setState(state => ({ ...state, selectedCustomersIds: customerId }))
        setConfirmPause(true)
    }

    const handleCancelBulk = () => {
        const customerId = selectedCustomers.map(customer => customer.businessCustomer.id)
        setState(state => ({ ...state, selectedCustomersIds: customerId }))
        setConfirmCancel(true)
    }

    const handleExportFilteredCustomers = () => {
        if (!isAdminOrOwner || !defMembership?.business?.id || exportingBusinessCustomersCsv) {
            return;
        }

        setCsvExportRequested(true);
        exportBusinessCustomersCsv(
            defMembership.business.id,
            buildBusinessCustomersExportQuery({
                searchText: submittedQuery,
                segmentId: selectedSegment,
                tags: selectedTags,
            }),
            t("business_customers.export_csv_filename")
        );
    };

    const handleExportSelectedCustomers = () => {
        if (!isAdminOrOwner || !selectedCustomers?.length) {
            return;
        }

        downloadSelectedCustomersCsv(
            selectedCustomers,
            t("business_customers.export_selected_csv_filename")
        );
    };

    if (fetchingBusinessCustomerSummary || fetchingBusinessCustomerSummarySearch) {
        return <Loading />;
    }

    const toggleInvite = () => {
        setCustomerNumber(null);
        setVisibleInvite(false)
    }

    const pauseSubscriptionActions = (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
                label={t('common.no')}
                icon="pi pi-times"
                className="p-button-text"
                id="pause_no_btn"
                onClick={() => { setConfirmPause(false) }}
            />
            <Button
                label={t('common.yes')}
                icon="pi pi-check"
                loading={singleCustomer ? pausingSingleSubscriptions : pausingBulkSubscriptions}
                id="pause_yes_btn"
                onClick={() => {
                    if (singleCustomer) {
                        pauseSingleCustomersSubscriptions(state.selectedCustomersIds, defMembership.id)
                        setPausedSingle(true)
                    } else {
                        pauseBulkCustomersSubscriptions(defMembership.id, state.selectedCustomersIds)
                        setPausedBulk(true)
                    }
                }}
                autoFocus
            />
        </div>
    );
    const cancelSubscriptionActions = (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
                label={t('common.no')}
                icon="pi pi-times"
                className="p-button-text" id="cancelSubs_no_btn"
                onClick={() => { setConfirmCancel(false) }}
            />
            <Button
                label={t('common.yes')}
                icon="pi pi-check"
                loading={singleCustomer ? cancellingSingleSubscriptions : cancellingBulkSubscriptions}
                id="cancelSubs_yes_btn"
                onClick={() => {
                    if (singleCustomer) {
                        cancelSingleCustomersSubscriptions(state.selectedCustomersIds, defMembership.id)
                        setCancelSingle(true)
                    } else {
                        cancelBulkCustomersSubscriptions(defMembership.id, state.selectedCustomersIds)
                        setCancelBulk(true)
                    }

                }}
                autoFocus
            />
        </div>
    );
    const okButton = () => {
        return (
            <div>
                <Button
                    label={t('common.ok')}
                    icon="pi pi-check"
                    onClick={() => { alreadyPaused ? setAlreadyPaused(false) : setAlreadyCancelled(false) }}
                    className="p-button-text" id="pause_yes_btn"
                />
            </div>
        )
    }

    const actionBodyTemplate = (rowData) => (
        <div className="car-buttons customer_table_action_button">
            <Button
                icon="pi pi-plus-circle"
                style={{ cursor: "pointer" }}
                tooltip={t("business_customers.invite")}
                tooltipOptions={{
                    position: "bottom",
                    mouseTrack: true,
                    mouseTrackTop: 15,
                }}
                className="p-button-warning p-button-rounded p-button-text mr-2 customerAction"
                onClick={() => {
                    setShowInviteSubscriber(true);
                    setVisibleInvite(true)
                    setCustomerNumber(rowData.businessCustomer?.phoneNo)
                    setSelectedSubscriber(rowData.businessCustomer)
                }}
            />
            <Button
                icon="pi pi-pencil"
                className="p-button-warning p-button-rounded p-button-text mr-2 customerAction"
                style={{ cursor: "pointer" }}
                tooltip={t("business_customers.edit_customer")}
                tooltipOptions={{
                    position: "bottom",
                    mouseTrack: true,
                    mouseTrackTop: 15,
                }}
                onClick={() => {
                    setDisplayBasic(true);
                    setState((state) => ({
                        ...state,
                        data: rowData,
                        id: rowData.id,
                        name: rowData.businessCustomer.name,
                        firstName: rowData.businessCustomer.firstName,
                        lastName: rowData.businessCustomer.lastName,
                        customerNum: rowData.businessCustomer.customerNum,
                        email: rowData.businessCustomer.email,
                    }));
                }}
            />
            <Button
                icon="pi pi-dollar"
                className="p-button-warning p-button-rounded p-button-text mr-2 customerAction"
                style={{ cursor: "pointer" }}
                tooltip={t("business_customers.collect_payment")}
                tooltipOptions={{ position: "bottom", mouseTrack: true, mouseTrackTop: 15 }}
                onClick={() => {
                    setState(prev => ({
                        ...prev, showCollectPopup: true, currentSubscriber: rowData, oneOffOnly: true
                    }))
                }}
            />
            <Button
                icon="pi pi-book"
                className="p-button-warning p-button-rounded p-button-text mr-2 customerAction"
                style={{ cursor: "pointer" }}
                tooltip={t("business_customers.record_pay")}
                tooltipOptions={{ position: "bottom", mouseTrack: true, mouseTrackTop: 15 }}
                onClick={() => {
                    setState(prev => ({
                        ...prev, showRecordPopup: true, currentSubscriber: rowData, oneOffOnly: true
                    }))
                }}
            />
        </div>
    );
    const onClickSearchBtn = () => {
        setSubmittedQuery(query);
        setState((state) => {
            return {
                ...state,
                isFiltered: true,
            };
        });
    };

    const onClickCancelBtn = () => {
        setState((state) => {
            return {
                ...state,
                isFiltered: false,
            };
        });
        setQuery("");
        setSubmittedQuery("");
    };

    const handleAddSingleSubToSeg = (data) => {
        setRowSubClicked(data);
        setAddSubscriberVisible(true);
    }

    const handleRemoveSingleSubToSeg = (data) => {
        setRowSubClicked(data);
        setRemoveSubscriberVisible(true);
    }

    const handleSingleTagAdd = (data) => {
        setDisplayAdd(true);
        setRowSubClicked(data);
    }
    const handleSingleTagRemove = (data) => {
        setDisplayRemove(true);
        setRowSubClicked(data);
        setSpecificTagClicked();
    }

    const customTemplate = {
        layout: 'RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport',
        RowsPerPageDropdown: (options) => {
            const dropdownOptions = [
                { label: '20', value: 20 },
                { label: '50', value: 50 },
                { label: '100', value: 100 },
                { label: '200', value: 200 }
            ];

            return (
                <div className="p-d-flex p-ai-center p-mr-3 p-2" >
                    <span className="" style={{ display: "flex", alignSelf: "center" }}>
                        {t("business_customers.item_labels")}{":"}{' '}
                    </span>
                    <Dropdown
                        value={state.size}
                        options={dropdownOptions}
                        onChange={(e) => {
                            const isRowsChanged = state.size !== e.value;
                            if (isRowsChanged) {
                                setState(prev => ({
                                    ...prev,
                                    first: 0,
                                    page: 0,
                                    size: e.value,
                                }));
                            }
                        }}
                        style={{ width: '70px', minWidth: '70px', border: "1px solid #495057" }}
                        panelClassName="custom-dropdown-panel"
                    />
                </div>
            );
        }
    };

    const customerInfoTemplate = (rowData) => {

        if (rowData) {
            const linkedToPrimary = rowData.businessCustomer?.linkedToPrimary;
            const linkStatus = linkedToPrimary
                ? t("business_customers.secondary")
                : t("business_customers.primary");
            const linkStatusClass = linkedToPrimary
                ? "customer-link-badge customer-link-badge--secondary"
                : "customer-link-badge customer-link-badge--primary";

            return <div className="customer_info">
                <span className="customerReference">{rowData.businessCustomer ? rowData.businessCustomer?.customerNum : <div>{null}</div>}</span>
                {rowData.businessCustomer && (
                    <span className={linkStatusClass}>{linkStatus}</span>
                )}
                <div className="customer_name_row">
                    <span className="customer_name">{rowData.businessCustomer ? rowData.businessCustomer?.name : <div>{null}</div>}</span>
                </div>
                <span className="customer_data">{removePlusFromNum(rowData.businessCustomer?.phoneNo)}</span>
                <span className="customer_data">{rowData.businessCustomer ? rowData.businessCustomer?.email : <div>{null}</div>}</span>

                <div>
                    {rowData.businessCustomer.segment ?
                        <div className="segment-main-div mb-2">
                            <div className="segment-tag-div">
                                <span className="segment-class">{rowData.businessCustomer.segment ? rowData.businessCustomer.segment?.name : null}</span>
                                <i className="pi pi-times-circle pl-2 tag-close-icon cursor-pointer" onClick={() => { handleRemoveSingleSubToSeg(rowData) }} />
                            </div>

                            <div className="flex gap-2">
                                <span onClick={() => { handleAddSingleSubToSeg(rowData); setSwitchSegVisible(true); }} className="switch">{t("segment.switch_label")}</span>
                            </div>
                        </div>
                        : <div className="mb-1">
                            <p className="switch"
                                onClick={() => { handleAddSingleSubToSeg(rowData); setSwitchSegVisible(false); }}>
                                {t("segment.add_segment")}</p>
                        </div>}

                    {rowData?.tags ?
                        <div className="mt-2 wrapping-div">
                            {rowData?.tags.map(tag => <Tag key={tag.id} onClick={() => setSpecificTagClicked(tag)} className="tag-div-main">
                                <div className="tag_div">
                                    <span className="tag-class">{tag.name}</span>
                                    <i className="pi pi-times-circle pl-1 tag-close-icon" onClick={() => { handleSingleTagRemove(rowData) }}></i>
                                </div>
                            </Tag>)}
                            <div className="flex gap-2">
                                <span onClick={() => { handleSingleTagAdd(rowData) }} className="add-tag-text">{t("tags.add_label")}</span>
                            </div>
                        </div> :
                        <div>
                            <span onClick={() => { handleSingleTagAdd(rowData) }} className="add-tag-text pt-1">{t("tags.add_label")}</span>
                        </div>}
                </div>
            </div >
        }
    }
    const customerIdTemplate = (rowData) => {
        if (rowData) {
            return <div>
                {
                    rowData.id ?
                        <div className="customer_main">
                            <span className="customerId" onClick={() => {
                                setState((state) => { return { ...state, customerId: rowData.businessCustomer.id } })
                                if (rowData.businessCustomer.id) routeChange(`/crm/customers/customer/${rowData.businessCustomer.id}`, rowData);
                            }}>{rowData.businessCustomer.reference}</span>
                            <span className="createdOnLabel">{t('business_customers.created_on')}</span>
                            <span className="createdOn">{formatDate(rowData.addedOn)}</span>
                        </div> : "--"
                }
            </div>
        }
    }

    const subscriptionAmountTemplate = (rowData) => {
        const policy = rowData.recentSub?.plan?.policy;

        if (policy === "CUSTOM" && rowData.recentSub?.cost != null) {
            return (
                <FormatCurrency
                    amount={rowData.recentSub.cost}
                    currencySymbol={currencySymbol}
                    currencyFormat={currencyFormat}
                    className="plan-amount"
                />
            );
        }

        if (policy === "FLEXIBLE" || rowData.recentSub?.plan?.flex) {
            return <span className="plan-amount">{t("business_customers.flex")}</span>;
        }

        if (rowData.recentSub?.plan?.cost) {
            return (
                <FormatCurrency
                    amount={rowData.lastPayment?.originalAmount}
                    currencySymbol={currencySymbol}
                    currencyFormat={currencyFormat}
                    className="plan-amount"
                />
            );
        }

        return null;
    };

    const nextBillingAmountTemplate = (rowData) => {
        const nextSub = rowData.nextScheduled?.subscription;
        const policy = nextSub?.plan?.policy;
        const amount = nextSub?.cost ?? (policy === "CUSTOM" ? rowData.recentSub?.cost : null);

        if (policy === "FLEXIBLE" || nextSub?.plan?.flex) {
            return <span className="scheduled-cost">{t("subscriber.flex_true")}</span>;
        }

        if (amount != null) {
            return (
                <FormatCurrency
                    id="flex-direction"
                    amount={amount}
                    currencySymbol={currencySymbol}
                    currencyFormat={currencyFormat}
                    className="scheduled-cost"
                />
            );
        }

        if (policy === "CUSTOM") {
            return <span className="scheduled-cost">{t("amount_fixed")}</span>;
        }

        return null;
    };

    const subscriptionBodyTemplate = (rowData) => {
        const totalSubs = (rowData.activeSubs + rowData.completedSubs + rowData.cancelledSubs + rowData.pausedSubs)
        if (rowData) {
            return <div className="main-subs-col-div">
                {rowData.recentSub !== null ? <div style={{ display: "flex", flexDirection: "row" }}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        {(rowData.recentSub?.status ?
                            <span className={`status-outline status-${(rowData.recentSub?.status ? rowData.recentSub?.status.toLowerCase() : '')} status-content-stat`}>
                                {rowData.recentSub?.status === "COMPLETE" ? t("business_customers.completed") : rowData.recentSub?.status}</span> : null
                        )}
                        <div className="subscription-status-column">
                            <div>
                                {totalSubs > 1 && <div style={{ display: "flex", flexDirection: "row" }} onClick={(e) => {
                                    handleOverlay(e, rowData.id)
                                }}>
                                    <p className="more-subs">{`${totalSubs} ${t('total_subs')}`}</p>
                                    <i className="pi pi-arrow-up-right arrow-subs"></i>
                                </div>}
                            </div>
                        </div>
                    </div>

                    <div className="subscription-column">
                        <span className="subscriptionReference" onClick={() => {
                            navigateToSubsDetails(rowData.recentSub.id)
                        }}>{rowData.recentSub?.subscriberReference}</span>
                        <div className="flex align-items-center gap-2">
                            <span className="plan_name">{rowData.recentSub?.plan?.name}</span>
                            <AutoBillingBadge collectionMode={rowData.recentSub?.plan?.collectionMode} />
                        </div>

                        {subscriptionAmountTemplate(rowData)}

                        <span className="plan-billingPeriod">{rowData.recentSub?.plan?.billingPeriod ? t(`product.${rowData.recentSub?.plan?.billingPeriod.toLowerCase()}`) : null}</span>

                        {rowData.lastPayment?.setupAmount && <div className="setup_fee_div">
                            <span className="setup_fee_tag">{t("common.setup_fee_label")}</span>
                            <FormatCurrency
                                amount={rowData.lastPayment?.setupAmount}
                                currencySymbol={currencySymbol}
                                currencyFormat={currencyFormat}
                                className="setup_fee_value"
                            />
                        </div>}
                    </div>
                </div> : null}
            </div>
        }
    }

    const paymentBodyTemplate = (rowData) => {
        if (rowData) {
            return <div className="payment-column-main">
                <div className="payment-column-1">
                    <div style={{ display: "flex", flexDirection: 'row', marginBottom: "2px" }}>
                        <span className="total-collected-count">{`${rowData.collectedCount} ${t(rowData.collectedCount > 1 ? "business_customers.collected_payments_label" : "business_customers.collected_payment")}`}</span>
                        {rowData.collectedCount > 0 ? (
                            <div
                                className="flex"
                                onClick={(e) => handleOverlayToggle(e, rowData)}
                            >
                                <span className="latest">{`${t('latest')}`}</span>
                                <i className="pi pi-arrow-up-right arrow-latest"></i>
                            </div>
                        ) : null}
                    </div>

                    <OverlayPanel
                        ref={(el) => {
                            if (el) {
                                overlayRefs.current.set(rowData.id, el);
                            } else {
                                overlayRefs.current.delete(rowData.id);
                            }
                        }}
                        showCloseIcon
                        className="subsOverlay"
                        onHide={() => setActiveOverlayId(null)}
                    >
                        <div className="latest-info">
                            <div className="flex align-items-center gap-2">
                                <span className="latest-name">{rowData.lastPayment?.plan?.name || 'N/A'}</span>
                                <AutoBillingBadge collectionMode={rowData.lastPayment?.plan?.collectionMode} />
                            </div>
                            <span className="latest-date">
                                {rowData.lastPayment?.receivedAt ? formatDate(rowData.lastPayment.receivedAt) : 'N/A'}
                            </span>
                            {rowData.lastPayment?.amount ? (
                                <FormatCurrency
                                    amount={rowData.lastPayment.amount}
                                    currencySymbol={currencySymbol}
                                    currencyFormat={currencyFormat}
                                    className="latest-amount"
                                />
                            ) : (
                                <span className="latest-amount">{t("amount_fixed")}</span>
                            )}
                        </div>
                    </OverlayPanel>

                    <div>
                        <div style={{ marginBottom: "2px", display: "flex", flexDirection: "row" }}>
                            <FormatCurrency
                                amount={rowData.collected}
                                currencySymbol={currencySymbol}
                                currencyFormat={currencyFormat}
                                className="total-collected"
                            />
                            <span className="collected-label">{`${t('business_customers.collected')}`}</span>
                        </div>

                        <div style={{ marginBottom: "1px", display: "flex", flexDirection: "row" }}>
                            <FormatCurrency
                                amount={rowData.revenue}
                                currencySymbol={currencySymbol}
                                currencyFormat={currencyFormat}
                                className="cus-revenue"
                            />
                            <span className="revenue-label">{`${t('business_customers.revenue')}`}</span>
                        </div>

                        <div>
                            <span className="missed-payment">
                                {rowData.missedCount}
                            </span>
                            <span className="missed-payment-label">{`${t('business_customers.missed_payments')}`}</span>
                        </div>
                    </div>
                </div>

                {(rowData.nextScheduled && rowData.nextScheduled?.subscription?.status === 'ACTIVE') && <div className="payment-column-2">
                    <span className="next-billing">{t('business_customers.next_billing')}</span>
                    <span className="scheduled-date">{formatDate(rowData.nextScheduled?.subscription.nextBillingDate)}</span>
                    {nextBillingAmountTemplate(rowData)}
                    {(rowData.nextScheduled?.subscription.successCount === 0 && rowData.nextScheduled?.subscription.plan.setup) && (
                        <div className="setup_fee_div">
                            <span className="setup_fee_tag">{t("common.setup_fee_label")}</span>
                            <FormatCurrency
                                amount={rowData.nextScheduled?.subscription?.plan?.setup}
                                currencySymbol={currencySymbol}
                                currencyFormat={currencyFormat}
                                className="setup_fee_value"
                            />
                        </div>
                    )}
                    <div className="flex align-items-center gap-2">
                        <span className="scheduled-plan-name">{rowData.nextScheduled?.subscription?.plan.name}</span>
                        <AutoBillingBadge collectionMode={rowData.nextScheduled?.subscription?.plan?.collectionMode} />
                    </div>
                </div>}
            </div>
        }
    }

    const subscribersMobileSummaryTemplate = (rowData) => (
        <div className="subscribers-mobile-summary">
            <div className="subscribers-mobile-section">
                {customerIdTemplate(rowData)}
            </div>
            <div className="subscribers-mobile-section">
                {customerInfoTemplate(rowData)}
            </div>
            <div className="subscribers-mobile-section">
                {subscriptionBodyTemplate(rowData)}
            </div>
            <div className="subscribers-mobile-section">
                {paymentBodyTemplate(rowData)}
            </div>
            {
                <div className="subscribers-mobile-action-row">
                    {actionBodyTemplate(rowData)}
                </div>
            }
        </div>
    );

    const checkboxColumn = {
        header: state.isFiltered ? (
            <Checkbox
                checked={
                    Array.isArray(selectedCustomers) &&
                    selectedCustomers.length > 0 &&
                    selectedCustomers.length ===
                    (state.filteredEnterpriseCustomersObjects?.content?.length ?? 0)
                }
                onChange={(e) => {
                    if (e.checked) {
                        setSelectedCustomers(
                            state.filteredEnterpriseCustomersObjects?.content || []
                        );
                        setRowSubClicked(null)
                    } else {
                        setSelectedCustomers([]);
                    }
                }}
            />
        ) : null,

        body: (rowData) => {
            const isChecked =
                Array.isArray(selectedCustomers) &&
                selectedCustomers.some((r) => r.id === rowData.id);

            return (
                <Checkbox
                    checked={isChecked}
                    onChange={(e) => {
                        if (e.checked) {
                            setSelectedCustomers((prev) =>
                                Array.isArray(prev) ? [...prev, rowData] : [rowData]
                            );
                            setRowSubClicked(null)
                        } else {
                            setSelectedCustomers((prev) =>
                                Array.isArray(prev)
                                    ? prev.filter((r) => r.id !== rowData.id)
                                    : []
                            );
                        }
                    }}
                />
            );
        },
        style: {
            border: "0px",
            borderBottom: "1px solid #e0e0e0",
            padding: "0px",
            alignContent: "center",
            width: "45px"
        },
        bodyClassName: "activity-select subscribers-mobile-select-column",
        headerClassName: "select-header check-box-header subscribers-mobile-select-column",
    };

    const desktopColumns = [
        {
            field: "customerId",
            header: t("business_customers.customer_id"),
            body: customerIdTemplate,
            alignHeader: "left",
            headerClassName: "customer_headers customer_id_header",
            sortable: true,
        },
        {
            field: "customerInfo",
            header: t("business_customers.customer_info"),
            body: customerInfoTemplate,
            sortable: false,
            headerClassName: "customer_headers customer_info_header",
        },
        {
            field: "subscriptionInfo",
            header: t("business_customers.subscription_info"),
            body: subscriptionBodyTemplate,
            alignHeader: "left",
            sortable: false,
            headerClassName: "customer_headers subscription_Info ",
        },
        {
            field: "payments",
            header: t("business_customers.payments"),
            body: paymentBodyTemplate,
            alignHeader: "left",
            sortable: false,
            headerClassName: "customer_headers payments",
        },
    ];

    const mobileSummaryColumn = {
        field: "mobileSummary",
        header: t("business_customers.customer_info"),
        body: subscribersMobileSummaryTemplate,
        headerClassName: "customer_headers customer_info_header",
        className: "subscribers-mobile-summary-column subscribers-mobile-expander-sibling",
    };

    const columns = [
        checkboxColumn,
        ...(isMobileStack ? [mobileSummaryColumn] : desktopColumns),
    ];


    const closeDisplayBasic = () => {
        setDisplayBasic(false)
    }

    const totalRecords = state.isFiltered ? state.filteredEnterpriseCustomersObjects?.totalElements : state.businessCustomers?.totalElements
    const filteredSize = state.filteredEnterpriseCustomersObjects?.totalElements;

    const isSelected = selectedCustomers?.length > 0;
    const count = selectedCustomers?.length;

    const removeCustomers = () => {
        setSelectedCustomers([]);
    }

    const handleSearch = (value) => {
        const inputValue = value ?? "";

        // eslint-disable-next-line
        const pattern = /^[a-zA-Z0-9\s\-\/]*$/;

        if (pattern.test(inputValue)) {
            setQuery(inputValue)
        }
    };

    const onPageChange = (e) => {
        const isRowsChanged = state.size !== e.rows;

        if (isRowsChanged) {
            setState(prev => ({
                ...prev,
                first: 0,
                page: 0,
                size: e.rows,
            }));
        } else {
            setState(prev => ({
                ...prev,
                first: e.first,
                page: e.page,
            }));
        }
    };

    const handleOnchange = (e) => {
        const value = e?.value;

        setSelectedSegment(value);

        const anyFilterActive = (selectedTags?.length > 0) || value || submittedQuery;

        setState(prev => ({
            ...prev,
            isFiltered: anyFilterActive
        }));
    };

    const handleSelectionChange = (e) => {
        const incoming = Array.isArray(e?.value) ? e.value : [];

        setSelectedTags(incoming);

        const anyFilterActive = incoming.length > 0 || selectedSegment;

        setState(prev => ({
            ...prev,
            isFiltered: anyFilterActive
        }));
    };

    const subscriptionItems = [
        {
            label: t("business_customers.sub"),
            items: [
                {
                    template: () => (
                        <Button
                            disabled={pausingBulkSubscriptions || pausingSingleSubscriptions || fetchingCustomerReceivedPayment}
                            className="activity-actions-count-btn p-button-outlined w-full justify-start pause-subs-menu"
                            onClick={() => handlePauseBulk()}
                        >
                            {pausingBulkSubscriptions ? t("subscriber.pausing") : t("subscriber.pause_btn")}
                        </Button>
                    )
                },
                {
                    template: () => (
                        <Button
                            disabled={cancellingBulkSubscriptions || cancellingSingleSubscriptions || fetchingCustomerReceivedPayment}
                            className="activity-actions-count-btn p-button-outlined w-full cancel-subs-menu"
                            onClick={() => handleCancelBulk()} >{cancellingBulkSubscriptions ? t("subscriber.cancelling") : t("subscriber.cancel_btn")}
                        </Button>
                    )
                }
            ]
        }
    ];
    const segmentActionItems = [
        {
            label: t("segment.label"),
            items: [
                {
                    template: () => (
                        <Button
                            className="w-full justify-start segment-menu p-button hover-blue-text"
                            onClick={() => setAddSubscriberVisible(true)}>{t('segment.add_segment_menu_item')}
                        </Button>
                    )
                },
                {
                    template: () => (
                        <Button
                            className="w-full justify-start segment-menu p-button hover-blue-text"
                            onClick={() => setRemoveSubscriberVisible(true)}>{t('segment.remove_segment_menu_item')}
                        </Button>
                    )
                }
            ]
        }
    ];

    const tagActionItems = [
        {
            label: t('tags.tag_items'),
            items: [
                {
                    template: () => (
                        <Button
                            className="w-full justify-start tag-menu hover-blue-text"
                            onClick={() => setDisplayAdd(true)}>{t('tags.add_tag_menu_item')}
                        </Button>
                    )
                },
                {
                    template: () => (
                        <Button
                            className="w-full justify-start tag-menu hover-blue-text"
                            onClick={() => setDisplayRemove(true)}>{t('tags.remove_tag_menu_item')}
                        </Button>
                    )
                }
            ]
        },
    ];

    const dropdownExportItems = [
        { name: t("payments.download_as_csv"), code: "CSV", alias: t("payments.csv_hint") },
    ];

    const exportItemsTemplate = (option) => {
        const handleExportClick = () => {
            if (option?.code === "CSV") {
                handleExportFilteredCustomers();
            }
        };

        return (
            <section className="flex flex-column mb-1" onClick={handleExportClick}>
                <div className="dropdown-export-temp pt-1">
                    <p className="m-0 option-name">{option.name}</p>
                    <i className="pi pi-download" />
                </div>
                <p className="m-0 option-alias">{option.alias}</p>
            </section>
        );
    };

    const subscriberDialogs = [
        {
            key: 1, visible: addSubscriberVisible, onHide: () => setAddSubscriberVisible(false),
            header: rowSubClicked && !switchSegVisible
                ? t("segment.add_subscriber_title")
                : switchSegVisible ? t("segment.switch_seg_") : t("segment.add_subscribers_title"),
            saveLabel: rowSubClicked && switchSegVisible ?
                t("segment.switch_seg_btn") : t("segment.add_subs"),
            removeLabelClicked: false
        },
        {
            key: 2, visible: removeSubscriberVisible, onHide: () => setRemoveSubscriberVisible(false), header: rowSubClicked ? t("segment.remove_subscriber_title") : t("segment.remove_subscribers_title"),
            saveLabel: t("segment.remove_subs"), removeLabelClicked: removeSubscriberVisible
        }
    ];

    return (
        <div className="customers-screen">
            {subscriberDialogs.map(({ key, ...dialog }) => (
                <SubscriberToSegment key={key} businessId={defMembership.business.id} setSelectedSubscribers={setSelectedCustomers} query={query}
                    selectedSubscribers={selectedCustomers} customersCall={getSummaryCustomers}
                    customerSearchCall={refreshCustomers}
                    pageOptions={pageOptions} rowSubClicked={rowSubClicked} {...dialog}
                />
            ))}

            {displayBasic && <CustomerEdit displayBasic={displayBasic} onHide={closeDisplayBasic} id={state.id} customerId={state.customerId} state={state} setState={setState}
                rowData={state.data} />}
            {!businessCustomers?.business?.enterprise ? (
                <div>
                    <Card className="search action_bar_cards mb-5 p-1">
                        {isSelected ?
                            <div className="m-2">
                                <span className="activity-actions-count-number count-style pb-3">
                                    {isSelected &&
                                        (selectedCustomers.length === 1 ? `${count} ${t("business_customers.selected_title_singular")}` : `${count} ${t("business_customers.selected_title")}`)}
                                    {!isSelected && `${t("business_customers.title")}`}
                                </span>
                                {selectedCustomers.length > 1 || selectedCustomers.length === 1 ? <hr className="m-0" style={{ borderTop: "2px solid #e0e0e0" }} /> : null}
                            </div>
                            : null
                        }

                        {isSelected ?
                            (<div className="main-menu-bar">
                                <section>
                                    {(defMembership.role === "OWNER" || defMembership.role === "ADMIN") &&
                                        (<div className="customer-actions-btns" >
                                            <Menubar
                                                className="custom-tag-menu no-bg-menubar"
                                                model={subscriptionItems}
                                                id="subscription-menubar"
                                            />
                                        </div>)}
                                </section>
                                {selectedCustomers.length > 1 ?
                                    <section className="menu_bar ml-4">
                                        <div className="flex flex-direction-row gap-2">
                                            <Menubar
                                                model={segmentActionItems}
                                                className="custom-tag-menu no-bg-menubar "
                                                id="segment-menubar"
                                            />
                                            <Menubar
                                                label={t("tags.create_tag_label")}
                                                model={tagActionItems}
                                                className="custom-tag-menu no-bg-menubar"
                                                id="tags-menubar"
                                            />
                                        </div>
                                    </section> : null}

                                {isAdminOrOwner && (
                                <section className="menu_bar subscribers-export-selected">
                                    {exportingBusinessCustomersCsv && (
                                        <ProgressSpinner className="mr-0" style={{ width: "2rem", height: "2rem" }} />
                                    )}
                                    <Button
                                        type="button"
                                        className="p-button-text subscribers-export-selected-btn"
                                        icon="pi pi-download"
                                        label={t(selectedCustomers.length === 1 ? "business_customers.export_customer" : "business_customers.export_customers")}
                                        disabled={exportingBusinessCustomersCsv}
                                        onClick={handleExportSelectedCustomers}
                                    />
                                </section>
                                )}

                                <p onClick={removeCustomers}
                                    className="cancel_selection_btn">
                                    <i class="pi pi-times cancel-icon"></i>{t("subscriber.cancel_selection_btn")}
                                </p>
                            </div>
                            )
                            :
                            (<section className="search-main-bar subscribers-filters-row">
                                <div className="subscribers-filters-search">
                                    <CustomerSearchField
                                        id="customers_search_input"
                                        placeholder={t("business_customers.label")}
                                        value={query}
                                        onChange={handleSearch}
                                        onSearch={onClickSearchBtn}
                                        onClear={onClickCancelBtn}
                                    />
                                </div>

                                <div className="subscribers-filter-field">
                                    <SegmentDropdown
                                        value={selectedSegment}
                                        options={segmentList}
                                        optionLabel="name"
                                        optionValue="id"
                                        filter
                                        showClear
                                        onChange={(e) => handleOnchange(e)}
                                        className="w-full activity-dropdown"
                                        placeholder={t("report.select_segment")}
                                    />
                                </div>

                                <div className="subscribers-filter-field subscribers-tags-field">
                                    <TagMultiselect
                                        className="sub_tag_select custom-multiselect subscribers-tags-multiselect w-full"
                                        panelClassName="tag-multiselect-panel"
                                        id="sub_tag_select_id"
                                        value={selectedTags}
                                        onChange={(e) => {
                                            handleSelectionChange(e);
                                        }}
                                        options={groupedOptions || []}
                                        optionLabel="label"
                                        optionValue="value"
                                        optionGroupLabel="label"
                                        optionGroupChildren="items"
                                        placeholder={t("tags.select_tag")}
                                        showSelectAll={false}
                                        display="chip"
                                    />
                                    {selectedTags && selectedTags.length > 0 && (
                                        <div
                                            onClick={() => {
                                                setSelectedTags([]);

                                                setState(prev => ({
                                                    ...prev,
                                                    isFiltered: selectedSegment ? true : false
                                                }));
                                            }}
                                            className="tags-clear-div">
                                            <i className="pi pi-times tags-clear-icon" />
                                        </div>)}
                                </div>

                                {isAdminOrOwner && (
                                <div className="subscribers-export-field">
                                    <div className="spin_div subscribers-export-row">
                                        {exportingBusinessCustomersCsv && (
                                            <ProgressSpinner className="mr-0" style={{ width: "3rem", height: "2rem" }} />
                                        )}
                                        <Dropdown
                                            placeholder={t("export")}
                                            style={{ width: "130px" }}
                                            options={dropdownExportItems}
                                            optionLabel="name"
                                            itemTemplate={exportItemsTemplate}
                                            disabled={exportingBusinessCustomersCsv}
                                            className="text-left export-dropdown items-center"
                                            id="export_drop"
                                        />
                                    </div>
                                </div>
                                )}
                            </section>
                            )}
                    </Card>

                    <AddSubscribersToTagDialog visible={displayAdd} onHide={() => setDisplayAdd(false)} selectedCustomers={selectedCustomers}
                        rowSubClicked={rowSubClicked} setSelectedCustomers={setSelectedCustomers}
                        refreshCustomers={refreshCustomers} />
                    <RemoveSubscribersDialog visible={displayRemove} onHide={() => setDisplayRemove(false)} selectedCustomers={selectedCustomers}
                        setSelectedCustomers={setSelectedCustomers} removeDialogClick={displayRemove}
                        rowSubClicked={rowSubClicked} specificTagClicked={specificTagClicked}
                        refreshCustomers={refreshCustomers} />

                    <CollectPayment
                        {...{ state, setState, countryCurrency, membershipId, defMembership }}
                        callBack={refreshCustomers}
                    />

                    <RecordPayment
                        {...{ state, setState, calenderDateFormat, countryCurrency, membershipId, defMembership }}
                        callBack={refreshCustomers} />

                    <CustomDataTable
                        key={`${JSON.stringify(selectedCustomers.map(c => c.id))}-${isMobileStack}`}
                        sortField="addedOn"
                        responsiveLayout="stack"
                        columns={columns}
                        selection={selectedCustomers}
                        onSelectionChange={(e) => {
                            setSelectionType(e?.type)
                            setSelectedCustomers(e.value);
                        }}
                        value={state.isFiltered ? state.filteredEnterpriseCustomersObjects?.content : state.businessCustomers?.content}
                        rowClassName="row-selection-disabled subscribers-data-table"
                    >
                        {!isMobileStack &&  (
                            <Column
                                headerClassName="datatable-header-default subscribers-action-column"
                                className="datatable-cell-default subscribers-action-column"
                                body={actionBodyTemplate}
                                style={{ minWidth: "1.5rem" }}
                            />
                        )}
                    </CustomDataTable>

                    <Paginator
                        first={state.first}
                        rows={state.isFiltered ? filteredSize : state.size}
                        totalRecords={totalRecords}
                        onPageChange={onPageChange}
                        template={customTemplate}
                        currentPageReportTemplate="{first} - {last} of {totalRecords}"
                        className="custom-paginator"
                    />

                </div>
            ) : (
                <div>{t("business_customers.no_display_message")}</div>
            )}
            {showInviteSubscriber && (
                <InviteSubscriber
                    visible={state.visible}
                    header={t("business_customers.invite")}
                    toggleVisible={visibleInvite}
                    onHide={toggleInvite}
                    phoneNo={customerNumber}
                    businessCustomer={selectedSubscriber}
                    selectedSubscriberSegment={selectedSubscriber}
                />
            )}

            <CustomToast
                {...toastData}
                trigger={toastTrigger}
                handleActionsAfterToast={() => {
                    refreshCustomers()
                    setSelectedCustomers([])
                    setToastTrigger(false)
                }}
                handleActionsAfterErrorToast={() => setToastTrigger(false)}
            />

            <Dialog
                visible={alreadyPaused}
                onHide={() => setAlreadyPaused(false)}
                header={t("subscriber.already_paused")}
                footer={okButton}
            >
                <h6 className="block-span">{t("subscriber.already_paused_text")} </h6>
            </Dialog>

            <Dialog
                visible={alreadyCancelled}
                onHide={() => setAlreadyCancelled(false)}
                header={t("subscriber.already_cancelled")}
                footer={okButton}
            >
                <h6 className="block-span">{t("subscriber.already_cancelled_text")} </h6>
            </Dialog>

            <Dialog
                visible={confirmCancel}
                style={{ width: "auto" }}
                onHide={() => setConfirmCancel(false)}
                header={t("subscriber.confirmation_header")}
                footer={cancelSubscriptionActions}
            >
                <h6>
                    <Trans
                        i18nKey={singleCustomer ? "business_customers.cancel_confirmation_message" : "business_customers.cancel_confirmation_message_multiple"}
                        components={{
                            3: <span className="inline-span-black" />, 1: <span className="inline-span-red" />,
                            2: <span className="inline-span-blue" />, 4: <br />
                        }}
                    />
                </h6>
            </Dialog>

            <Dialog
                visible={confirmPause}
                style={{ width: "auto" }}
                onHide={() => setConfirmPause(false)}
                header={t("subscriber.confirmation_header")}
                footer={pauseSubscriptionActions}
            >
                <h6>
                    <Trans
                        i18nKey={singleCustomer ? "business_customers.pause_confirmation_message" : "business_customers.pause_confirmation_message_multiple"}
                        components={{
                            3: <span className="inline-span-black" />, 1: <span className="inline-span-red" />,
                            2: <span className="inline-span-blue" />, 4: <br />
                        }}
                    />
                </h6>
            </Dialog>

            <OverlayPanel
                ref={loadingOverlayRef}
                dismissable={false}
                closeOnEscape={false}
            >
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '10px',
                    minWidth: '150px'
                }}>
                    <ProgressSpinner style={{ width: '15px', height: '10px' }} />
                </div>
            </OverlayPanel>

            <OverlayPanel
                showCloseIcon
                className="subsOverlay"
                ref={overlayPanelMoreSubs}
                onHide={hideCustomerSubscriptions}
            >
                {overlayContent}
            </OverlayPanel>
        </div>
    );
};

export default Customers;